import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { CONTACT } from "@/constants/content";
import { scrollToSection } from "@/hooks/useLenis";

const LINKS = [
  { href: "#about", label: "About" },
  { href: "#highlights", label: "Highlights" },
  { href: "#why-invest", label: "Why Invest" },
  { href: "#location", label: "Location" },
  { href: "#site-plan", label: "Site Plan" },
  { href: "#pricing", label: "Pricing" },
  { href: "#central-heights", label: "Apartments" },
  { href: "#gallery", label: "Gallery" },
  { href: "#faq", label: "FAQ" },
];

export const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  const go = (href) => {
    setOpen(false);
    requestAnimationFrame(() => scrollToSection(href));
  };

  return (
    <header
      className={`cp-nav ${scrolled || open ? "cp-nav--solid" : ""} ${open ? "cp-nav--menu-open" : ""}`}
      data-testid="site-header"
    >
      <div className="cp-nav-inner">
        <button className="cp-nav-brand" onClick={() => go("#hero")} data-testid="nav-logo-btn">
          <img src="/brochure/icon-mark.png" alt="Buildaxis Developers" />
          <span>
            <strong>BUILDAXIS</strong>
            <em>CENTRAL PLAZA</em>
          </span>
        </button>

        <nav className="cp-nav-links" aria-label="Primary">
          {LINKS.map((l) => (
            <button key={l.href} onClick={() => go(l.href)} data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}>
              {l.label}
            </button>
          ))}
        </nav>

        <div className="cp-nav-cta">
          <a href={`tel:${CONTACT.tel}`} className="cp-nav-tel" data-testid="nav-call-link">
            {CONTACT.phone1}
          </a>
          <button className="btn btn-gold btn-sm" onClick={() => go("#enquire")} data-testid="nav-enquire-btn">
            Enquire Now
          </button>
        </div>

        <button
          type="button"
          className="cp-nav-burger"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          aria-controls="cp-nav-mobile-menu"
          aria-label={open ? "Close menu" : "Open menu"}
          data-testid="nav-mobile-toggle"
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <>
            <motion.button
              key="nav-backdrop"
              type="button"
              className="cp-nav-mobile-backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={() => setOpen(false)}
              aria-label="Close menu"
              data-testid="nav-mobile-backdrop"
            />
            <motion.nav
              key="nav-panel"
              id="cp-nav-mobile-menu"
              className="cp-nav-mobile"
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }}
              data-testid="nav-mobile-menu"
            >
              {LINKS.map((l) => (
                <button
                  key={l.href}
                  type="button"
                  onClick={() => go(l.href)}
                  data-testid={`nav-mobile-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                >
                  {l.label}
                </button>
              ))}
              <a href={`tel:${CONTACT.tel}`} className="cp-nav-mobile-call" data-testid="nav-mobile-call-link">
                Call {CONTACT.phone1}
              </a>
              <button type="button" className="btn btn-gold cp-nav-mobile-enquire" onClick={() => go("#enquire")} data-testid="nav-mobile-enquire-btn">
                Enquire Now
              </button>
            </motion.nav>
          </>
        )}
      </AnimatePresence>
    </header>
  );
};
